import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Trophy, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';

const FORMATS = [
  { v: 'knockout',     l: 'Knockout (2/4/8 squads)', hint: '4 squads → 3 matches → 1 champion' },
  { v: 'round_robin',  l: 'Round-Robin League',       hint: 'Everyone plays everyone · points table' },
  { v: 'league_split', l: 'Seasonal Split',            hint: 'Round-robin with seasonal identity (e.g. Summer Split)' },
];

const SPORTS = ['Football', 'Cricket', 'Badminton', 'Basketball', 'Pickleball', 'Tennis'];

export default function ArenaxTournamentCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', format: 'knockout', sport: 'Football',
    city: 'Bengaluru', locality: '', max_squads: 4,
    season_label: '', starts_on: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Give your tournament a name'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Use numeric max_squads
      payload.max_squads = Number(payload.max_squads);
      if (!payload.locality) delete payload.locality;
      if (!payload.season_label) delete payload.season_label;
      if (!payload.starts_on) delete payload.starts_on;
      const r = await api.post('/arenax/tournaments', payload);
      toast.success('Tournament created — invite your squads!');
      navigate(`/arenax/tournaments/${r.data.tournament.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create tournament');
    } finally { setSaving(false); }
  };

  return (
    <ArenaxLayout>
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="arenax-tournament-create">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="h-4 w-4 text-amber-300" />
          <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">START A LEAGUE</span>
        </div>
        <h1 className="text-3xl font-black text-slate-100 mb-1">Build your hyperlocal tournament.</h1>
        <p className="text-sm text-slate-400 mb-8">
          Keep it small. Keep it local. Recurring brackets compound into seasonal identity.
        </p>

        <div className="space-y-5 rounded-2xl border border-slate-800 p-6"
          style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.12), var(--ax-card-bg-soft))' }}>
          <Field label="Tournament name" hint="e.g. HSR Saturday Knockout">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              data-testid="arenax-tournament-name-input"
              placeholder="HSR Saturday Knockout"
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none" />
          </Field>

          <Field label="Format">
            <div className="grid grid-cols-1 gap-2">
              {FORMATS.map(f => (
                <button key={f.v} onClick={() => setForm({ ...form, format: f.v })}
                  data-testid={`arenax-tournament-format-${f.v}`}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    form.format === f.v ? 'border-amber-300/60 bg-amber-300/10' : 'border-slate-700 hover:border-slate-600'
                  }`}>
                  <div className={`text-sm font-bold ${form.format === f.v ? 'text-amber-200' : 'text-slate-100'}`}>{f.l}</div>
                  <div className="text-[10px] text-slate-500">{f.hint}</div>
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Sport">
              <select value={form.sport} onChange={e => setForm({ ...form, sport: e.target.value })}
                data-testid="arenax-tournament-sport-input"
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 focus:border-cyan-500/60 focus:outline-none">
                {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Max squads">
              <select value={form.max_squads} onChange={e => setForm({ ...form, max_squads: e.target.value })}
                data-testid="arenax-tournament-max-input"
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 focus:border-cyan-500/60 focus:outline-none">
                {form.format === 'knockout' ? [2, 4, 8].map(n => <option key={n} value={n}>{n}</option>)
                  : [3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="City">
              <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                data-testid="arenax-tournament-city-input"
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 focus:border-cyan-500/60 focus:outline-none" />
            </Field>
            <Field label="Locality (hyperlocal)" hint="HSR, Koramangala, Indiranagar">
              <input value={form.locality} onChange={e => setForm({ ...form, locality: e.target.value })}
                data-testid="arenax-tournament-locality-input"
                placeholder="HSR"
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none" />
            </Field>
          </div>

          {form.format === 'league_split' && (
            <Field label="Season label" hint='e.g. "Summer Split 2026"'>
              <input value={form.season_label} onChange={e => setForm({ ...form, season_label: e.target.value })}
                placeholder="Summer Split 2026"
                data-testid="arenax-tournament-season-input"
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none" />
            </Field>
          )}

          <Field label="Starts on (optional)">
            <input type="date" value={form.starts_on} onChange={e => setForm({ ...form, starts_on: e.target.value })}
              data-testid="arenax-tournament-starts-input"
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 focus:border-cyan-500/60 focus:outline-none" />
          </Field>

          <button onClick={submit} disabled={saving}
            data-testid="arenax-tournament-create-submit"
            className="w-full px-4 py-3 rounded-lg text-sm font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400 hover:brightness-110 transition-all disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : '🏆 Launch tournament'}
          </button>
        </div>
      </div>
    </ArenaxLayout>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold block mb-1">
        {label}{hint && <span className="text-slate-500 font-normal normal-case tracking-normal ml-2">· {hint}</span>}
      </label>
      {children}
    </div>
  );
}
